/** Blue Max canvas art (C). Projection and drawing never modify simulation state. */
import {
    WIDTH, HEIGHT, RUNWAY_X, RUNWAY_HALF_WIDTH, SERVICE_START, SERVICE_END,
} from './blueMaxEngine';

export const COLORS = {
    grass: '#789650', field: '#8fa45b', darkField: '#6a8947', river: '#377f9d', water: '#63aab5',
    bank: '#c8b477', road: '#b7a177', runway: '#596458', shadow: '#40523d', plane: '#88d2e9',
    enemy: '#bd624e', roof: '#d0bd90', wall: '#8b7559', flame: '#ffc965',
};

export function project(x, y, z = 0, cameraY = 0) {
    const ahead = y - cameraY;
    return { x: 128 + (x - 110) * 0.85 + ahead * 0.25, y: 220 - ahead * 0.7 - z };
}

function polygon(ctx, points, color) {
    ctx.fillStyle = color;
    ctx.beginPath();
    points.forEach((point, i) => {
        if (i === 0) ctx.moveTo(Math.round(point.x), Math.round(point.y));
        else ctx.lineTo(Math.round(point.x), Math.round(point.y));
    });
    ctx.closePath();
    ctx.fill();
}

function patch(ctx, camera, x1, y1, x2, y2, color, z = 0) {
    polygon(ctx, [project(x1, y1, z, camera), project(x2, y1, z, camera),
        project(x2, y2, z, camera), project(x1, y2, z, camera)], color);
}

function drawRunway(ctx, camera, start, end) {
    patch(ctx, camera, RUNWAY_X - RUNWAY_HALF_WIDTH - 4, start - 5,
        RUNWAY_X + RUNWAY_HALF_WIDTH + 4, end + 5, COLORS.bank);
    patch(ctx, camera, RUNWAY_X - RUNWAY_HALF_WIDTH, start,
        RUNWAY_X + RUNWAY_HALF_WIDTH, end, COLORS.runway);
    for (let y = start + 15; y < end - 10; y += 32) {
        patch(ctx, camera, RUNWAY_X - 1, y, RUNWAY_X + 1, y + 13, '#dddac1');
    }
    [start + 8, end - 12].forEach((y) => {
        patch(ctx, camera, RUNWAY_X - 18, y, RUNWAY_X + 18, y + 4, '#dddac1');
    });
}

function drawTerrain(ctx, state, frame) {
    const camera = state.plane.y;
    ctx.fillStyle = COLORS.grass;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    const start = Math.floor((camera - 180) / 80) * 80;
    for (let y = start; y < camera + 520; y += 80) {
        const alternate = Math.abs(Math.round(y / 80)) % 2 === 0;
        patch(ctx, camera, -200, y, 42, y + 78, alternate ? COLORS.field : COLORS.darkField);
        patch(ctx, camera, 104, y, 350, y + 78, alternate ? COLORS.grass : COLORS.field);
    }
    patch(ctx, camera, 40, camera - 200, 104, camera + 550, COLORS.bank);
    patch(ctx, camera, 44, camera - 200, 100, camera + 550, COLORS.river);
    patch(ctx, camera, 110, camera - 200, 118, camera + 550, COLORS.road);
    for (let y = start; y < camera + 500; y += 36) {
        const x = 56 + (Math.abs(y) % 23);
        patch(ctx, camera, x, y + (frame % 40) * 0.15, x + 9, y + 2, COLORS.water);
    }
    drawRunway(ctx, camera, 0, 220);
    drawRunway(ctx, camera, SERVICE_START, SERVICE_END);
}

function drawPlane(ctx, point, color, frame, shadow = false) {
    ctx.save();
    ctx.translate(Math.round(point.x), Math.round(point.y));
    ctx.rotate(0.35);
    ctx.fillStyle = color;
    ctx.fillRect(-3, -11, 6, 25);
    ctx.fillRect(-16, -5, 32, 5);
    ctx.fillRect(-13, 2, 26, 4);
    ctx.fillRect(-7, 10, 14, 3);
    if (!shadow) {
        ctx.fillStyle = '#e6dcae';
        ctx.fillRect(-2, -11, 4, 4);
        ctx.fillStyle = '#273f42';
        ctx.fillRect(-2, 0, 4, 4);
        ctx.fillStyle = '#e3e5d5';
        ctx.fillRect(frame % 4 < 2 ? -8 : -5, -14, frame % 4 < 2 ? 16 : 10, 1);
        ctx.fillRect(-11, -4, 3, 3);
        ctx.fillRect(8, -4, 3, 3);
    }
    ctx.restore();
}

function drawGroundTarget(ctx, target, camera, frame) {
    const { x, y, width, depth, height } = target;
    const base = project(x, y, 0, camera);
    if (!target.alive) {
        ctx.fillStyle = COLORS.shadow;
        ctx.fillRect(base.x - 7, base.y - 3, 14, 6);
        return;
    }
    if (target.type === 'boat') {
        polygon(ctx, [project(x, y + 16, 0, camera), project(x + 8, y, 0, camera),
            project(x + 6, y - 14, 0, camera), project(x - 6, y - 14, 0, camera),
            project(x - 8, y, 0, camera)], '#354b51');
        patch(ctx, camera, x - 4, y - 5, x + 4, y + 7, '#c3c6ad', 4);
    } else {
        const top = project(x, y, height, camera);
        polygon(ctx, [project(x - width, y - depth, 0, camera), project(x + width, y - depth, 0, camera),
            project(x + width, y - depth, height, camera),
            project(x - width, y - depth, height, camera)], COLORS.wall);
        patch(ctx, camera, x - width, y - depth, x + width, y + depth,
            target.type === 'bridge' ? '#beab82' : COLORS.roof, height);
        ctx.fillStyle = '#61543e';
        if (target.type === 'gun') {
            ctx.fillRect(top.x - 4, top.y - 3, 8, 6);
            ctx.fillRect(top.x - 1, top.y - 12, 3, 12);
        } else if (target.type === 'building') {
            ctx.fillRect(top.x - 5, top.y, 3, 4);
            ctx.fillRect(top.x + 3, top.y, 3, 4);
        }
    }
    if (target.marked) {
        const mark = project(x, y, height + 7, camera);
        ctx.fillStyle = frame % 40 < 20 ? '#fbe9a5' : '#d8493f';
        ctx.fillRect(mark.x - 4, mark.y - 4, 8, 8);
        ctx.fillStyle = '#963b37';
        ctx.fillRect(mark.x - 2, mark.y - 2, 4, 4);
    }
}

export function drawList(state) {
    const camera = state.plane.y;
    return [
        ...state.targets.map(target => ({ ...target, kind: 'ground' })),
        ...state.fighters.filter(enemy => enemy.alive && enemy.active).map(enemy => ({ ...enemy, kind: 'plane' })),
        { ...state.plane, kind: 'player' },
        ...state.bullets.map(bullet => ({ ...bullet, kind: 'bullet' })),
        ...state.bombs.map(bomb => ({ ...bomb, kind: 'bomb' })),
        ...state.explosions.map(explosion => ({ ...explosion, kind: 'explosion' })),
    ].filter(item => item.y > camera - 180 && item.y < camera + 550)
        .sort((a, b) => (-a.y + a.z) - (-b.y + b.z));
}

export function render(ctx, state, frame = 0) {
    const camera = state.plane.y;
    drawTerrain(ctx, state, frame);
    const list = drawList(state);
    list.filter(item => item.kind === 'plane' || item.kind === 'player').forEach((item) => {
        drawPlane(ctx, project(item.x, item.y, 0, camera), COLORS.shadow, frame, true);
    });
    list.forEach((item) => {
        const point = project(item.x, item.y, item.z, camera);
        if (item.kind === 'ground') drawGroundTarget(ctx, item, camera, frame);
        else if (item.kind === 'plane') drawPlane(ctx, point, COLORS.enemy, frame);
        else if (item.kind === 'player') {
            if (state.status === 'gameOver') {
                ctx.fillStyle = COLORS.shadow;
                ctx.fillRect(point.x - 9, point.y - 4, 18, 8);
            } else if (item.immunity <= 0 || frame % 8 < 4) {
                drawPlane(ctx, point, COLORS.plane, frame);
                if (item.condition < 2) {
                    ctx.fillStyle = '#a1a499';
                    ctx.fillRect(point.x - 5, point.y + 18 + frame % 5, 5, 5);
                }
            }
        } else if (item.kind === 'explosion') {
            const size = 5 + (0.5 - item.life) * 25;
            ctx.fillStyle = item.life > 0.2 ? COLORS.flame : '#a87447';
            ctx.fillRect(point.x - size / 2, point.y - size / 2, size, size);
            ctx.fillStyle = '#f6edb8';
            ctx.fillRect(point.x - 2, point.y - 2, 4, 4);
        } else {
            ctx.fillStyle = item.kind === 'bomb' ? '#343e35' : item.enemy ? '#ffb95a' : '#f5f0c6';
            ctx.fillRect(point.x - 1, point.y - 2, item.kind === 'bomb' ? 4 : 2, 5);
            if (item.kind === 'bomb') {
                const shadow = project(item.x, item.y, 0, camera);
                ctx.fillStyle = COLORS.shadow;
                ctx.fillRect(shadow.x - 1, shadow.y, 3, 2);
            }
        }
    });
}
